import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline';

export default function OverwrittenTasks() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialTasks = location.state?.overwrittenTasks ?? [];
  const [tasks, setTasks] = useState(initialTasks);

  // Group tasks by attendee
  const grouped = tasks.reduce((acc, task) => {
    const key = task.attendee || task.Name || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(task);
    return acc;
  }, {});

  const handleIgnore = async (taskId) => {
    try {
      await fetch(`https://api.insightos.co.uk/api/deletetask/${taskId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      setTasks((prev) => prev.filter((t) => t.TaskID !== taskId));
    } catch (err) {
      console.error('Failed to delete task', err);
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return String(timeStr).substring(0, 5);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d)) return String(dateStr).substring(0, 10);
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-500/20 border border-amber-400/30">
            <ExclamationTriangleIcon className="h-6 w-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Overwritten Tasks</h1>
            <p className="text-sm text-slate-400">
              The following tasks were pushed past shift end when the meeting was created. Choose what to do with each.
            </p>
          </div>
        </div>

        {tasks.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-12 text-center">
            <p className="text-slate-400 text-lg">All overwritten tasks have been resolved.</p>
            <button
              onClick={() => navigate('/dashboard')}
              className="mt-6 inline-flex items-center rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-500 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        ) : (
          <>
            {Object.entries(grouped).map(([attendee, attendeeTasks]) => (
              <div key={attendee} className="mb-8 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl overflow-hidden">
                {/* Attendee header */}
                <div className="px-6 py-4 border-b border-white/10 bg-white/5">
                  <h2 className="text-base font-semibold text-white">{attendee}</h2>
                  <p className="text-xs text-slate-400 mt-0.5">
                    {attendeeTasks.length} task{attendeeTasks.length !== 1 ? 's' : ''} overwritten
                  </p>
                </div>

                {/* Task rows */}
                <div className="divide-y divide-white/5">
                  {attendeeTasks.map((task) => (
                    <div key={task.TaskID} className="px-6 py-4">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        {/* Task details */}
                        <div className="flex-1 min-w-0 space-y-1.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-white truncate">{task.Task}</span>
                            {task.Step && (
                              <span className="inline-flex items-center rounded-full bg-indigo-500/20 px-2 py-0.5 text-xs font-medium text-indigo-300 border border-indigo-400/20">
                                Step {task.Step}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
                            <span className="font-medium text-slate-300">{formatDate(task.TaskDate)}</span>
                            <span>{formatTime(task.StartTime)} – {formatTime(task.EndTime)}</span>
                            {task.ProcessName && <span className="text-slate-500">· {task.ProcessName}</span>}
                          </div>
                          {task.Description && (
                            <p className="text-xs text-slate-400 line-clamp-2">{task.Description}</p>
                          )}
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => navigate('/overtime-manager')}
                            className="rounded-xl border border-violet-400/30 bg-violet-500/10 px-3 py-1.5 text-xs font-semibold text-violet-300 hover:bg-violet-500/20 transition-colors whitespace-nowrap"
                          >
                            Request Overtime
                          </button>
                          <button
                            onClick={() => navigate('/taskcalendar')}
                            className="rounded-xl border border-sky-400/30 bg-sky-500/10 px-3 py-1.5 text-xs font-semibold text-sky-300 hover:bg-sky-500/20 transition-colors whitespace-nowrap"
                          >
                            Add to Later Date
                          </button>
                          <button
                            onClick={() => handleIgnore(task.TaskID)}
                            className="flex items-center gap-1 rounded-xl border border-red-400/30 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-400 hover:bg-red-500/20 transition-colors"
                          >
                            <XMarkIcon className="h-3.5 w-3.5" />
                            Ignore
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex justify-end mt-2">
              <button
                onClick={() => navigate('/dashboard')}
                className="rounded-xl border border-white/10 bg-white/5 px-5 py-2.5 text-sm font-semibold text-slate-300 hover:bg-white/10 transition-colors"
              >
                Skip — Go to Dashboard
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
